import React, { useState, useEffect, useRef } from 'react';
import {
  Edit,
  X,
  FileText,
  Download,
  AlertCircle,
  ClipboardList,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Send,
  CheckCircle,
  XCircle,
  Copy
} from 'lucide-react';
import { GenericListView, FormPageLayout, DocumentUpload, LoadingSpinner, PrimaryButtonLight, getDisciplinePillClassName, ConfirmDialog, useToast } from '../common';
import Dialog from '../common/Dialog';
import type { ActionButtonConfig, DocumentUploadDoc } from '../common';
import { useHasAnyPermission } from '../../hooks/usePermission';
import {
  POCCertificationsService,
  mapUiDisciplineToApiDiscipline
} from '../../services/pocCertifications.service';
import type {
  POCCertificationData,
  CreatePOCCertificationRequest
} from '../../services/pocCertifications.service';
import type { Cms485Version, MethodPocSent, POCCertStatus } from '../../dtos/clinical/poc-certification.dto';
import SignedCopyUpload from './documents/SignedCopyUpload';
import { UnsignedBadge, unsignedDeadline } from './orders/orderDeadline';
import { fetchDemographics } from '../../services/patientmr/demographics.service';
import { AssessmentsService } from '../../services/patientmr/assessments.service';
import { CarePlansService } from '../../services/patientmr/careplans.service';
import { ApiError } from '../../dtos/common/api.dto';
import { pocMissingItems, PocMissingItem } from './pocMissingItems';
import { disciplineCodes, disciplineLabel, getDisciplineEntry } from '../../constants/disciplines';

// Type alias for component use
type POCCertification = POCCertificationData;

interface POCCertificationsProps {
  expanded: boolean;
  toggleExpanded: () => void;
  patientId?: string;
  /** Certification to open once the list loads (dashboard alert deep link, R-209) */
  focusId?: string;
  /** Called once focusId has been handled (opened or not found) */
  onFocusHandled?: () => void;
}

const POC_STATUS_LABELS: Record<string, string> = {
  draft:             'Draft',
  pending_signature: 'Pending Signature',
  signed:            'Signed',
  rejected:          'Rejected',
  expired:           'Expired',
};

/** ORD-04: unsigned badge for a draft / pending-signature POC, counted from creation */
export function pocUnsignedDeadline(cert: Pick<POCCertificationData, 'status' | 'createdAt' | 'deadline'>, today: Date = new Date()) {
  if (cert.status !== 'draft' && cert.status !== 'pending_signature') return null;
  return unsignedDeadline(cert.createdAt, cert.deadline, today);
}

const formatDateTime = (iso?: string): string => (iso ? new Date(iso).toLocaleString() : '');

/** Uploaded documents that have not been removed (removed ones come back with deletedAt set) */
export function liveDocuments<T extends { deletedAt?: string | null }>(docs?: T[] | null): T[] {
  return (docs || []).filter((d) => !d.deletedAt);
}

/** A document can be removed only from a draft, and never the copy recorded as signed */
export function canRemovePocDocument(
  cert: Pick<POCCertificationData, 'status' | 'signedDocumentId'>,
  documentId?: string
): boolean {
  return cert.status === 'draft' && !!documentId && documentId !== cert.signedDocumentId;
}

const POCCertifications: React.FC<POCCertificationsProps> = ({
  expanded,
  toggleExpanded,
  patientId: propPatientId,
  focusId,
  onFocusHandled
}) => {
  // Permission checks for POC certifications
  const canCreatePocCertification = useHasAnyPermission([
    'patientmr:phi:poc-certification:create' as any,
    'patientmr:phi:poc-certification:createall' as any
  ]);

  const canReadPocCertification = useHasAnyPermission([
    'patientmr:phi:poc-certification:read' as any,
    'patientmr:phi:poc-certification:readall' as any
  ]);

  const canUpdatePocCertification = useHasAnyPermission([
    'patientmr:phi:poc-certification:update' as any,
    'patientmr:phi:poc-certification:updateall' as any
  ]);

  // Read-only mode: has read but no write permissions
  const isReadOnlyView = canReadPocCertification && !canCreatePocCertification && !canUpdatePocCertification;

  const [certifications, setCertifications] = useState<POCCertification[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Phase 19 F1: what the plan of care still needs before a CMS-485 can be generated (422 POC_MODEL_INCOMPLETE)
  const [pocMissing, setPocMissing] = useState<PocMissingItem[]>([]);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  // Form states
  const [showAddForm, setShowAddForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingCert, setEditingCert] = useState<POCCertification | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // Phase 4 machine actions (Phase 8 §3.8)
  const [confirmAction, setConfirmAction] = useState<'send' | 'reject' | null>(null);
  const [showSignDialog, setShowSignDialog] = useState(false);
  const [signedDocChoice, setSignedDocChoice] = useState('');
  const [actionBusy, setActionBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [removeDoc, setRemoveDoc] = useState<DocumentUploadDoc | null>(null);
  const [removingDocId, setRemovingDocId] = useState<string | null>(null);
  const { showToast } = useToast();

  // Linked entity dropdown data (populated when Linked Docs section opens)
  const [linkedEntityAssessments, setLinkedEntityAssessments] = useState<Record<string, any[]>>({});
  const [linkedEntityCarePlans, setLinkedEntityCarePlans] = useState<Record<string, any[]>>({});
  const [linkedEntityLoading, setLinkedEntityLoading] = useState(false);

  // Collapsible section open/close state
  const [physicianSectionOpen, setPhysicianSectionOpen] = useState(false);
  const [signatureSectionOpen, setSignatureSectionOpen] = useState(false);
  const [linkedDocsSectionOpen, setLinkedDocsSectionOpen] = useState(false);


  const hasLoadedCertificationsRef = useRef<string | null>(null);

  const patientId = propPatientId;

  // Load certifications from API
  useEffect(() => {
    const fetchCertifications = async () => {
      if (!patientId || loading) return;

      // Prevent duplicate loads
      if (hasLoadedCertificationsRef.current === patientId) {
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await POCCertificationsService.listPOCCertifications(patientId);
        setCertifications((response.certifications || []));
        setHasLoadedOnce(true);

        hasLoadedCertificationsRef.current = patientId;
      } catch (err: unknown) {
        console.error('Error loading POC certifications:', err);
        setError((err as Error).message || 'Failed to load POC certifications. Please try again.');
        setHasLoadedOnce(true);

        // Mark as loaded even on error to prevent continuous retries
        // User can manually refresh using the refresh button
        hasLoadedCertificationsRef.current = patientId;
      } finally {
        setLoading(false);
      }
    };

    if (expanded && patientId) {
      fetchCertifications();
    }

    if (!expanded) {
      setShowAddForm(false);
      setIsEditing(false);
      setEditingCert(null);
    }
    // `loading` is a re-entry guard only; adding it would refetch after every save
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, expanded]);

  // Manual refresh
  const handleRefresh = async () => {
    if (!patientId || refreshing) return;

    try {
      setRefreshing(true);
      setError(null);

      hasLoadedCertificationsRef.current = null;

      const response = await POCCertificationsService.listPOCCertifications(patientId);
      setCertifications((response.certifications || []));
      setHasLoadedOnce(true);

      hasLoadedCertificationsRef.current = patientId;
    } catch (err: unknown) {
      console.error('Error refreshing POC certifications:', err);
      setError((err as Error).message || 'Failed to refresh POC certifications. Please try again.');
    } finally {
      setRefreshing(false);
    }
  };

  // Open form for new certification (async for physician auto-populate)
  const openAddForm = async (basedOn?: POCCertification, mode: 'version' | 'recert' = 'version') => {
    setShowAddForm(true);
    setIsEditing(false);
    setNotice(null);

    // Phase 19 F14: recertification drafts the next 60-day period (day after the signed period ends)
    // with the same physician; order dates and links are for the new period's assessment and plan.
    if (basedOn && mode === 'recert') {
      const addDays = (iso: string, days: number) => {
        const d = new Date(`${String(iso).slice(0, 10)}T00:00:00Z`);
        d.setUTCDate(d.getUTCDate() + days);
        return d.toISOString().slice(0, 10);
      };
      const start = basedOn.certificationPeriodEnd ? addDays(basedOn.certificationPeriodEnd, 1) : '';
      setEditingCert({
        id: 0,
        certificationPeriodStart: start,
        certificationPeriodEnd: start ? addDays(start, 59) : '',
        discipline: basedOn.discipline,
        disciplines: basedOn.disciplines,
        status: 'draft' as POCCertStatus,
        generatedPOCContent: '',
        documents: [],
        physicianName: basedOn.physicianName,
        physicianNpi: basedOn.physicianNpi,
        physicianPhone: basedOn.physicianPhone,
        physicianFax: basedOn.physicianFax,
        physicianAddress: basedOn.physicianAddress,
      });
      setNotice('Recertification drafted for the next period. Link the reassessment and the updated care plan, then generate the plan of care.');
      setPhysicianSectionOpen(true);
      setSignatureSectionOpen(false);
      setLinkedDocsSectionOpen(true);
      return;
    }

    if (basedOn) {
      // New version after a rejection: same period, physician and links; a fresh draft
      setEditingCert({
        id: 0,
        certificationPeriodStart: basedOn.certificationPeriodStart,
        certificationPeriodEnd: basedOn.certificationPeriodEnd,
        discipline: basedOn.discipline,
        disciplines: basedOn.disciplines,
        status: 'draft' as POCCertStatus,
        generatedPOCContent: '',
        documents: [],
        physicianName: basedOn.physicianName,
        physicianNpi: basedOn.physicianNpi,
        physicianPhone: basedOn.physicianPhone,
        physicianFax: basedOn.physicianFax,
        physicianAddress: basedOn.physicianAddress,
        orderDate: basedOn.orderDate,
        orderReceivedDate: basedOn.orderReceivedDate,
        verbalOrderDate: basedOn.verbalOrderDate,
        linkedAssessments: basedOn.linkedAssessments,
        linkedCarePlans: basedOn.linkedCarePlans,
      });
      setPhysicianSectionOpen(true);
      setSignatureSectionOpen(false);
      setLinkedDocsSectionOpen(false);
      return;
    }

    // Physician auto-populate from demographics
    let physicianDefaults: Partial<POCCertification> = {};
    if (patientId) {
      try {
        const demographics = await fetchDemographics(patientId);
        physicianDefaults = {
          physicianName: demographics?.pcpName || '',
          physicianNpi: demographics?.pcpNpi || '',
          physicianPhone: demographics?.pcpPhone || ''
        };
      } catch (err) {
        // Non-blocking — form opens with blank physician fields
        console.error('Failed to fetch demographics for physician auto-populate:', (err as Error).message);
      }
    }

    setEditingCert({
      id: 0,
      certificationPeriodStart: new Date().toISOString().split('T')[0],
      // ORD-06: a 60-day period ends on start + 59
      certificationPeriodEnd: new Date(Date.now() + 59 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      discipline: 'RN',
      disciplines: ['RN'],
      status: 'draft' as POCCertStatus,
      generatedPOCContent: '',
      documents: [],
      ...physicianDefaults
    });
    setPhysicianSectionOpen(true);
    setSignatureSectionOpen(false);
    setLinkedDocsSectionOpen(false);
  };

  // Open form for editing
  const openEditForm = (id: number | string) => {
    const certToEdit = certifications.find(cert => cert.id === id);
    if (certToEdit) {
      setEditingCert({ ...certToEdit });
      setIsEditing(true);
      setShowAddForm(false);
      setPhysicianSectionOpen(true);
      setSignatureSectionOpen(false);
      setLinkedDocsSectionOpen(false);
      setNotice(null);
    }
  };

  // Alert deep link (R-209): open the certification with that id once the list has loaded
  const handledFocusRef = useRef<string | null>(null);
  useEffect(() => {
    if (!focusId) {
      handledFocusRef.current = null;
      return;
    }
    if (!expanded || !hasLoadedOnce || loading || handledFocusRef.current === focusId) return;
    handledFocusRef.current = focusId;
    const cert = certifications.find(
      (c) => c.pocCertificationId === focusId || String(c.id) === focusId
    );
    if (cert) {
      openEditForm(cert.id);
    } else {
      setError('The plan of care from the alert could not be found.');
    }
    onFocusHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- openEditForm is recreated every render; the ref runs this once per focusId
  }, [focusId, expanded, hasLoadedOnce, loading, certifications, onFocusHandled]);

  // Close form
  const cancelForm = () => {
    setShowAddForm(false);
    setIsEditing(false);
    setEditingCert(null);
    setNotice(null);
    setLinkedEntityAssessments({});
    setLinkedEntityCarePlans({});
  };

  // Handle lazy-load for Linked Clinical Documents section
  const handleLinkedDocsSectionOpen = async () => {
    setLinkedDocsSectionOpen(prev => {
      if (prev) return false; // toggle off
      return true;
    });
    if (linkedDocsSectionOpen || linkedEntityLoading || Object.keys(linkedEntityAssessments).length > 0) return;
    if (!patientId) return;
    try {
      setLinkedEntityLoading(true);
      const [assessmentsResp, carePlansResp] = await Promise.all([
        AssessmentsService.getAssessments(patientId),
        CarePlansService.getCarePlans(patientId)
      ]);
      const assessmentsByDisc: Record<string, any[]> = {};
      const carePlansByDisc: Record<string, any[]> = {};
      (assessmentsResp?.assessments || []).forEach((a: any) => {
        const key = a.discipline;
        assessmentsByDisc[key] = [...(assessmentsByDisc[key] || []), a];
      });
      (carePlansResp?.carePlans || []).forEach((cp: any) => {
        const key = cp.discipline;
        carePlansByDisc[key] = [...(carePlansByDisc[key] || []), cp];
      });
      setLinkedEntityAssessments(assessmentsByDisc);
      setLinkedEntityCarePlans(carePlansByDisc);
    } catch (err) {
      console.error('Failed to load linked entity dropdowns:', (err as Error).message);
    } finally {
      setLinkedEntityLoading(false);
    }
  };

  /** Open a stored file of this certification (uploaded document or CMS-485 version) */
  const openCertFile = async (fileId: string, fallbackName: string) => {
    if (!patientId || !editingCert) return;
    const certId = editingCert.pocCertificationId || editingCert.id;
    if (!certId) return;
    try {
      // Only generated files (CMS-485 versions) open through here
      const details = await POCCertificationsService.getDocumentSignedUrl(patientId, certId.toString(), fileId, { fileType: 'generated' });
      if (details.signedUrl) {
        const link = document.createElement('a');
        link.href = details.signedUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.download = details.documentName || fallbackName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download the file');
    }
  };

  /** Reload the certification being viewed (after an upload or a state change) and the list */
  const reloadEditingCert = async (certId: string) => {
    if (!patientId) return;
    const fresh = await POCCertificationsService.getPOCCertification(patientId, certId);
    setEditingCert(fresh);
    hasLoadedCertificationsRef.current = null;
    const listResponse = await POCCertificationsService.listPOCCertifications(patientId);
    setCertifications(listResponse.certifications || []);
    hasLoadedCertificationsRef.current = patientId;
  };

  const editingCertId = editingCert
    ? (editingCert.pocCertificationId || (editingCert.id ? editingCert.id.toString() : ''))
    : '';

  // Signed-copy upload (pre-signed POST; Phase 8 replaces the base64 PUT)
  const handleUploadSignedCopy = async (file: File, onProgress: (pct: number) => void) => {
    if (!patientId || !editingCertId) throw new Error('Save the certification before uploading the signed copy.');
    const doc = await POCCertificationsService.uploadSignedCopy(patientId, editingCertId, file, onProgress);
    await reloadEditingCert(editingCertId);
    if (doc.documentId) setSignedDocChoice(doc.documentId);
    setNotice('Signed copy uploaded. Use "Mark signed" to record the signature.');
  };

  // State machine: draft → pending_signature → signed | rejected
  const runTransition = async (action: 'send' | 'sign' | 'reject') => {
    if (!patientId || !editingCert || !editingCertId) return;
    setActionBusy(true);
    setError(null);
    try {
      if (action === 'send') {
        await POCCertificationsService.sendForSignature(patientId, editingCertId, {
          datePocSentToPhysician: editingCert.datePocSentToPhysician,
          methodPocSent: editingCert.methodPocSent,
        });
        setNotice('Sent for signature.');
      } else if (action === 'sign') {
        await POCCertificationsService.markSigned(patientId, editingCertId, signedDocChoice, {
          physicianSignatureDate: editingCert.physicianSignatureDate,
          dateSignedPotReceived: editingCert.dateSignedPotReceived,
        });
        setNotice('Marked signed.');
      } else {
        await POCCertificationsService.reject(patientId, editingCertId);
        setNotice('Rejected. Create a new version to send a corrected plan of care.');
      }
      await reloadEditingCert(editingCertId);
      setConfirmAction(null);
      setShowSignDialog(false);
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'The action failed. Try again.');
      setConfirmAction(null);
    } finally {
      setActionBusy(false);
    }
  };

  const startSendForSignature = () => {
    if (!editingCert) return;
    const missing: string[] = [];
    if (!editingCert.datePocSentToPhysician) missing.push('Date POC Sent to Physician');
    if (!editingCert.methodPocSent) missing.push('Method POC Sent');
    if (missing.length > 0) {
      setSignatureSectionOpen(true);
      setError(`To send for signature, fill in ${missing.join(' and ')} under Signature Tracking.`);
      return;
    }
    setConfirmAction('send');
  };

  // Remove an uploaded document (draft only; never the signed copy). The server returns 409 otherwise.
  const confirmRemoveDocument = async () => {
    const target = removeDoc;
    if (!patientId || !editingCertId || !target?.id) return;
    setRemovingDocId(target.id);
    try {
      await POCCertificationsService.deletePOCDocument(patientId, editingCertId, target.id);
      if (signedDocChoice === target.id) setSignedDocChoice('');
      showToast('Document removed.', { variant: 'success' });
      setRemoveDoc(null);
      await reloadEditingCert(editingCertId);
    } catch (err) {
      showToast(err instanceof Error && err.message ? err.message : 'Failed to remove the document', { variant: 'error' });
      setRemoveDoc(null);
    } finally {
      setRemovingDocId(null);
    }
  };

  const startMarkSigned = () => {
    const docs = liveDocuments(editingCert?.documents);
    if (docs.length === 0) {
      setError('Upload the physician-signed copy first.');
      return;
    }
    setSignedDocChoice((prev) => (prev && docs.some(d => d.documentId === prev) ? prev : docs[docs.length - 1].documentId || ''));
    setShowSignDialog(true);
  };

  // Download a document from the signed-copy list
  const handleDownloadSignedDocument = async (picked?: DocumentUploadDoc) => {
    if (!patientId || !editingCert) return;

    try {
      const docs = liveDocuments(editingCert.documents);
      const doc = docs.find(d => d.documentId === picked?.id) ?? (docs.length > 0 ? docs[0] : null);
      if (!doc || !doc.documentId) {
        setError('No document available to download');
        return;
      }

      const certId = editingCert.pocCertificationId || editingCert.id;
      if (!certId) {
        setError('Certification ID not available');
        return;
      }

      // Fetch document details including fresh signed URL
      const documentDetails = await POCCertificationsService.getDocumentSignedUrl(
        patientId,
        certId.toString(),
        doc.documentId
      );

      if (documentDetails.signedUrl) {
        // Trigger download using the signed URL
        const link = document.createElement('a');
        link.href = documentDetails.signedUrl;
        link.download = documentDetails.documentName || doc.documentName || 'document';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      setError(error instanceof Error ? error.message : 'Failed to download document');
    }
  };

  // Add discipline
  const handleAddDiscipline = (disciplineToAdd: string) => {
    if (editingCert) {
      const currentDisciplines = editingCert.disciplines || [editingCert.discipline].filter(Boolean);
      if (!currentDisciplines.includes(disciplineToAdd)) {
        const newDisciplines = [...currentDisciplines, disciplineToAdd];
        setEditingCert({
          ...editingCert,
          disciplines: newDisciplines,
          discipline: newDisciplines[0] // Keep first as primary for backward compatibility
        });
      }
    }
  };

  // Remove discipline
  const handleRemoveDiscipline = (disciplineToRemove: string) => {
    if (editingCert) {
      const currentDisciplines = editingCert.disciplines || [editingCert.discipline].filter(Boolean);
      const newDisciplines = currentDisciplines.filter(d => d !== disciplineToRemove);
      if (newDisciplines.length > 0) {
        setEditingCert({
          ...editingCert,
          disciplines: newDisciplines,
          discipline: newDisciplines[0] // Update primary discipline
        });
      }
    }
  };

  // Generate POC CMS-485 form
  const handleGeneratePOC = async () => {
    console.log('[GeneratePOC] clicked — editingCert:', editingCert?.pocCertificationId ?? editingCert?.id, 'patientId:', patientId);
    if (!editingCert || !patientId) {
      console.warn('[GeneratePOC] guard: editingCert or patientId missing', { editingCert: !!editingCert, patientId });
      return;
    }

    // Can only generate for existing certifications with an ID
    const certId = editingCert.pocCertificationId || editingCert.id;
    if (!certId) {
      console.warn('[GeneratePOC] guard: no certId (unsaved cert)');
      setError('Please save the certification before generating the POC form.');
      return;
    }
    console.log('[GeneratePOC] proceeding with certId:', certId);

    try {
      setIsGenerating(true);
      setError(null);

      // Map UI disciplines to API format for the request
      const mapDisciplineToApi = (uiDiscipline: string): string => {
        if (uiDiscipline === 'RN') return 'RN';
        return uiDiscipline; // PT, OT, ST, MSW, HHA remain the same
      };

      // Determine which discipline(s) to generate
      // If multiple disciplines, use 'ALL', otherwise use the specific discipline
      let disciplineToGenerate = 'ALL';
      if (editingCert.disciplines && editingCert.disciplines.length > 0) {
        // If only one discipline, generate for that specific one
        if (editingCert.disciplines.length === 1) {
          disciplineToGenerate = mapDisciplineToApi(editingCert.disciplines[0]);
        } else {
          // Multiple disciplines - generate combined form
          disciplineToGenerate = 'ALL';
        }
      } else if (editingCert.discipline) {
        // Fallback to primary discipline
        disciplineToGenerate = mapDisciplineToApi(editingCert.discipline);
      }

      const response = await POCCertificationsService.generateCMS485(
        patientId,
        certId.toString(),
        disciplineToGenerate
      );

      // Extract generated file information from response
      // Service returns response.data directly (already unwrapped)
      const generatedFile = response?.generatedFile;
      const cms485Form = response?.cms485Form;

      // Update the editing certification with the generated content and file info
      if (generatedFile || cms485Form) {
        setEditingCert({
          ...editingCert,
          generatedPOCContent: cms485Form ? JSON.stringify(cms485Form, null, 2) : editingCert.generatedPOCContent,
          generatedFiles: generatedFile ? [
            ...(editingCert.generatedFiles || []),
            {
              fileId: generatedFile.fileId,
              fileName: generatedFile.fileName,
              s3Key: generatedFile.s3Key,
              contentType: generatedFile.contentType,
              discipline: generatedFile.discipline,
              generatedAt: generatedFile.generatedAt,
              generatedBy: generatedFile.generatedBy,
              fileType: generatedFile.fileType
            }
          ] : editingCert.generatedFiles
        });
      }

      // Reload the certification: the new CMS-485 version appears in cms485Versions
      await reloadEditingCert(certId.toString());

    } catch (err: unknown) {
      console.error('Error generating POC form:', err instanceof ApiError ? err.domainCode : 'error');
      const missing = err instanceof ApiError && err.domainCode === 'POC_MODEL_INCOMPLETE' ? pocMissingItems(err.details) : [];
      setPocMissing(missing);
      setError(missing.length
        ? 'The CMS-485 was not generated. Complete these items first:'
        : (err as Error).message || 'Failed to generate POC form. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Save certification
  const handleSave = async () => {
    if (!editingCert || !patientId) return;

    // Status changes go through the action buttons (Send for signature, Mark signed, Reject);
    // Save only records field edits (draft) or signature details (pending signature).
    try {
      setLoading(true);
      setError(null);

      const certData: CreatePOCCertificationRequest = {
        certificationPeriodStart: editingCert.certificationPeriodStart,
        certificationPeriodEnd: editingCert.certificationPeriodEnd,
        discipline: editingCert.discipline,
        disciplines: editingCert.disciplines || [editingCert.discipline].filter(Boolean),
        status: 'draft',
        physicianName: editingCert.physicianName,
        physicianNpi: editingCert.physicianNpi,
        physicianPhone: editingCert.physicianPhone,
        physicianFax: editingCert.physicianFax,
        physicianAddress: editingCert.physicianAddress,
        orderDate: editingCert.orderDate,
        orderReceivedDate: editingCert.orderReceivedDate,
        verbalOrderDate: editingCert.verbalOrderDate,
        datePocSentToPhysician: editingCert.datePocSentToPhysician,
        methodPocSent: editingCert.methodPocSent as MethodPocSent | undefined,
        physicianSignatureDate: editingCert.physicianSignatureDate,
        dateSignedPotReceived: editingCert.dateSignedPotReceived,
        careCoordinatorName: editingCert.careCoordinatorName,
        careCoordinatorSignatureDate: editingCert.careCoordinatorSignatureDate,
        linkedAssessments: editingCert.linkedAssessments,
        linkedCarePlans: editingCert.linkedCarePlans
      };

      if (isEditing) {
        // Update existing
        const certId = editingCert.pocCertificationId || editingCert.id;
        if (!certId) {
          throw new Error('Certification ID not available');
        }
        // No status here: the generic update never moves the state machine
        await POCCertificationsService.updatePOCCertification(patientId, certId.toString(), { ...certData, status: undefined });
      } else {
        // Add new
        await POCCertificationsService.createPOCCertification(patientId, certData);
      }

      // Refresh the list
      hasLoadedCertificationsRef.current = null;
      const response = await POCCertificationsService.listPOCCertifications(patientId);
      setCertifications((response.certifications || []));

      cancelForm();
    } catch (err: unknown) {
      console.error('Error saving POC certification:', err);
      setError((err as Error).message || 'Failed to save POC certification. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Get status badge styles
  const getStatusBadgeStyles = (status: string): string => {
    return POCCertificationsService.getStatusBadgeStyles(status);
  };

  // Format date
  const formatDate = (dateString: string): string => {
    return POCCertificationsService.formatDate(dateString);
  };

  // Alert banner for no patient selected
  const alertBanner = !patientId ? (
    <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
      <div className="flex items-start">
        <AlertCircle className="h-5 w-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-amber-700">
          Please select a patient to view and manage POC certifications.
        </p>
      </div>
    </div>
  ) : undefined;

  if ((showAddForm || isEditing) && editingCert) {
    const status = editingCert.status;
    const canAct = canUpdatePocCertification && isEditing && !!editingCertId;
    const signedDocs: DocumentUploadDoc[] = liveDocuments(editingCert.documents)
      .map(d => ({
        id: d.documentId || '',
        name: d.documentName || 'Document',
        uploadedAt: d.uploadedAt,
        size: d.size,
        deletable: canAct && canRemovePocDocument(editingCert, d.documentId),
      }));
    const anyRemovable = signedDocs.some(d => d.deletable);
    // Phase 4 machine: fields edit only in draft; signature details also while pending signature
    const lockFields = isReadOnlyView || (isEditing && status !== 'draft');
    const lockSignature = isReadOnlyView || (isEditing && status !== 'draft' && status !== 'pending_signature');
    const nothingEditable = isEditing && status !== 'draft' && status !== 'pending_signature';
    const versions: Cms485Version[] = [...(editingCert.cms485Versions || [])].sort((a, b) => b.version - a.version || a.discipline.localeCompare(b.discipline));
    const deadline = isEditing ? pocUnsignedDeadline(editingCert) : null;

    const formActions: ActionButtonConfig[] = [
      {
        key: 'cancel',
        label: nothingEditable ? 'Close' : 'Cancel',
        variant: 'secondary',
        onClick: cancelForm,
        disabled: loading,
      },
      {
        key: 'save',
        label: isEditing ? 'Update Certification' : 'Add Certification',
        loadingLabel: 'Saving...',
        variant: 'primary',
        onClick: handleSave,
        loading: loading,
        disabled: loading,
        // Hide save in read-only mode and once nothing can be edited
        hidden: isReadOnlyView || nothingEditable,
      },
    ];

    return (
      <FormPageLayout
        title={`${isEditing ? 'Edit' : 'New'} POC Certification`}
        onBack={cancelForm}
        actions={formActions}
      >
        <div className="space-y-6">
          {/* Read-only mode notification */}
          {isReadOnlyView && (
            <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-md">
              <p className="text-sm text-gray-700">
                You do not have permission to edit POC Certifications. Viewing in read-only mode.
              </p>
            </div>
          )}

          {/* Error banner */}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md" role="alert">
              <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
              <div className="text-sm text-red-700 flex-1">
                <span>{error}</span>
                {pocMissing.length > 0 && (
                  <ul className="mt-1 list-disc pl-5 space-y-0.5">
                    {pocMissing.map(item => (
                      <li key={`${item.discipline || ''}-${item.field}`}>
                        {item.discipline ? `${item.discipline}: ` : ''}{item.label}
                        <span className="text-red-500"> ({item.where})</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <button type="button" onClick={() => { setError(null); setPocMissing([]); }} className="text-red-400 hover:text-red-600 shrink-0" aria-label="Dismiss error">
                <X size={14} />
              </button>
            </div>
          )}
          {notice && (
            <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-md" role="status">
              <span className="text-sm text-green-800 flex-1">{notice}</span>
              <button type="button" onClick={() => setNotice(null)} className="text-green-600 shrink-0" aria-label="Dismiss">
                <X size={14} />
              </button>
            </div>
          )}

          {/* Status and workflow actions (draft → pending signature → signed / rejected) */}
          <div className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid="poc-workflow">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-slate-700">Status</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeStyles(status)}`} data-testid="poc-status">
                {POC_STATUS_LABELS[status] ?? status}
              </span>
              <UnsignedBadge deadline={deadline} />
              {status === 'signed' && editingCert.signedAt && (
                <span className="text-xs text-slate-500">Signed copy recorded {formatDateTime(editingCert.signedAt)}</span>
              )}
            </div>
            {!isEditing && (
              <p className="text-xs text-slate-500">Save the draft first; then send it to the physician for signature.</p>
            )}
            {canAct && (
              <div className="flex flex-wrap gap-2">
                {status === 'draft' && (
                  <button type="button" onClick={startSendForSignature} disabled={actionBusy}
                    className="inline-flex min-h-[44px] items-center gap-1.5 rounded-md sm:min-h-0 bg-slate-800 px-3 py-2 text-sm text-white hover:bg-slate-700 disabled:opacity-50">
                    <Send size={14} aria-hidden="true" /> Send for signature
                  </button>
                )}
                {status === 'pending_signature' && (
                  <>
                    <button type="button" onClick={startMarkSigned} disabled={actionBusy}
                      className="inline-flex min-h-[44px] items-center gap-1.5 rounded-md sm:min-h-0 bg-green-700 px-3 py-2 text-sm text-white hover:bg-green-800 disabled:opacity-50">
                      <CheckCircle size={14} aria-hidden="true" /> Mark signed
                    </button>
                    <button type="button" onClick={() => setConfirmAction('reject')} disabled={actionBusy}
                      className="inline-flex min-h-[44px] items-center gap-1.5 rounded-md sm:min-h-0 border border-red-300 px-3 py-2 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50">
                      <XCircle size={14} aria-hidden="true" /> Reject
                    </button>
                  </>
                )}
                {(status === 'signed' || status === 'expired') && canCreatePocCertification && (
                  <button type="button" onClick={() => { const base = editingCert; cancelForm(); void openAddForm(base, 'recert'); }}
                    className="inline-flex min-h-[44px] items-center gap-1.5 rounded-md sm:min-h-0 border border-teal-300 px-3 py-2 text-sm text-teal-800 hover:bg-teal-50">
                    <Copy size={14} aria-hidden="true" /> Start recertification
                  </button>
                )}
                {status === 'rejected' && canCreatePocCertification && (
                  <button type="button" onClick={() => { const base = editingCert; cancelForm(); void openAddForm(base); }}
                    className="inline-flex min-h-[44px] items-center gap-1.5 rounded-md sm:min-h-0 border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                    <Copy size={14} aria-hidden="true" /> Create new version
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Disciplines */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            {/* Disciplines */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5 text-left">
                Disciplines <span className="text-red-500">*</span>
              </label>

              {/* Add Discipline Dropdown */}
              <div className="relative mb-2">
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      handleAddDiscipline(e.target.value);
                      e.target.value = ''; // Reset selection
                    }
                  }}
                  disabled={lockFields}
                  className={`w-full border border-slate-300 rounded-md px-3 py-2 text-sm text-left focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-colors appearance-none bg-white ${lockFields ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                  defaultValue=""
                >
                  <option value="">Add discipline...</option>
                  {disciplineCodes().filter(d => !(editingCert.disciplines || [editingCert.discipline].filter(Boolean)).includes(d)).map(disc => (
                    <option key={disc} value={disc}>{disciplineLabel(disc)}</option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                  <ChevronDown size={16} className="text-gray-500" />
                </div>
              </div>

              {/* Selected Disciplines */}
              {(editingCert.disciplines || [editingCert.discipline].filter(Boolean)).length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {(editingCert.disciplines || [editingCert.discipline].filter(Boolean)).map((disc) => (
                    <span
                      key={disc}
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getDisciplinePillClassName(disc)}`}
                    >
                      {disciplineLabel(disc)}
                      <button
                        type="button"
                        onClick={() => handleRemoveDiscipline(disc)}
                        className="ml-1.5 hover:opacity-70 focus:outline-none"
                        disabled={(editingCert.disciplines || [editingCert.discipline].filter(Boolean)).length === 1 || isReadOnlyView}
                        title={(editingCert.disciplines || [editingCert.discipline].filter(Boolean)).length === 1 ? 'At least one discipline is required' : 'Remove discipline'}
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Certification Period */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5 text-left">
                Certification Period Start <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={editingCert.certificationPeriodStart || ''}
                onChange={(e) => setEditingCert({ ...editingCert, certificationPeriodStart: e.target.value })}
                disabled={lockFields}
                className={`w-full border border-slate-300 rounded-md px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-colors bg-white ${lockFields ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5 text-left">
                Certification Period End <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={editingCert.certificationPeriodEnd || ''}
                onChange={(e) => setEditingCert({ ...editingCert, certificationPeriodEnd: e.target.value })}
                disabled={lockFields}
                className={`w-full border border-slate-300 rounded-md px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-colors bg-white ${lockFields ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
              />
            </div>
          </div>

          {/* Physician Information Section */}
          <div className="border border-gray-200 rounded-lg">
            <button
              type="button"
              onClick={() => setPhysicianSectionOpen(prev => !prev)}
              className="w-full flex items-center justify-between px-4 py-3 text-left font-medium text-gray-700 hover:bg-gray-50"
            >
              <span>Physician Information</span>
              {physicianSectionOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {physicianSectionOpen && (
              <div className="px-4 pb-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Row 1: physicianName, physicianNpi */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Physician Name</label>
                  <input
                    type="text"
                    value={editingCert?.physicianName || ''}
                    onChange={e => setEditingCert(prev => prev ? { ...prev, physicianName: e.target.value } : null)}
                    disabled={lockFields}
                    className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm ${lockFields ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                    placeholder="Physician full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Physician NPI</label>
                  <input
                    type="text"
                    value={editingCert?.physicianNpi || ''}
                    onChange={e => setEditingCert(prev => prev ? { ...prev, physicianNpi: e.target.value } : null)}
                    disabled={lockFields}
                    className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm ${lockFields ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                    placeholder="10-digit NPI"
                  />
                </div>
                {/* Physician order (ORD-01) */}
                <div>
                  <label htmlFor="poc-order-date" className="block text-sm font-medium text-gray-700 mb-1">Order Date</label>
                  <input
                    id="poc-order-date"
                    type="date"
                    value={editingCert?.orderDate || ''}
                    onChange={e => setEditingCert(prev => prev ? { ...prev, orderDate: e.target.value } : null)}
                    disabled={lockFields}
                    className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm ${lockFields ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                  />
                </div>
                <div>
                  <label htmlFor="poc-order-received" className="block text-sm font-medium text-gray-700 mb-1">Order Received</label>
                  <input
                    id="poc-order-received"
                    type="date"
                    value={editingCert?.orderReceivedDate || ''}
                    onChange={e => setEditingCert(prev => prev ? { ...prev, orderReceivedDate: e.target.value } : null)}
                    disabled={lockFields}
                    className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm ${lockFields ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                  />
                </div>
                {/* Row 2: physicianPhone, physicianFax */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Physician Phone</label>
                  <input
                    type="text"
                    value={editingCert?.physicianPhone || ''}
                    onChange={e => setEditingCert(prev => prev ? { ...prev, physicianPhone: e.target.value } : null)}
                    disabled={lockFields}
                    className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm ${lockFields ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Physician Fax</label>
                  <input
                    type="text"
                    value={editingCert?.physicianFax || ''}
                    onChange={e => setEditingCert(prev => prev ? { ...prev, physicianFax: e.target.value } : null)}
                    disabled={lockFields}
                    className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm ${lockFields ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                  />
                </div>
                {/* Row 3: street address (full width) */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address Street</label>
                  <input
                    type="text"
                    value={editingCert?.physicianAddress?.street || ''}
                    onChange={e => setEditingCert(prev => prev ? {
                      ...prev,
                      physicianAddress: { ...prev.physicianAddress, street: e.target.value }
                    } : null)}
                    disabled={lockFields}
                    className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm ${lockFields ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                  />
                </div>
                {/* Row 4: city, state, zip */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input
                    type="text"
                    value={editingCert?.physicianAddress?.city || ''}
                    onChange={e => setEditingCert(prev => prev ? {
                      ...prev,
                      physicianAddress: { ...prev.physicianAddress, city: e.target.value }
                    } : null)}
                    disabled={lockFields}
                    className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm ${lockFields ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                    <input
                      type="text"
                      value={editingCert?.physicianAddress?.state || ''}
                      onChange={e => setEditingCert(prev => prev ? {
                        ...prev,
                        physicianAddress: { ...prev.physicianAddress, state: e.target.value }
                      } : null)}
                      disabled={lockFields}
                      className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm ${lockFields ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                      maxLength={2}
                      placeholder="TX"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Zip</label>
                    <input
                      type="text"
                      value={editingCert?.physicianAddress?.zip || ''}
                      onChange={e => setEditingCert(prev => prev ? {
                        ...prev,
                        physicianAddress: { ...prev.physicianAddress, zip: e.target.value }
                      } : null)}
                      disabled={lockFields}
                      className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm ${lockFields ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Signature Tracking Section */}
          <div className="border border-gray-200 rounded-lg">
            <button
              type="button"
              onClick={() => setSignatureSectionOpen(prev => !prev)}
              className="w-full flex items-center justify-between px-4 py-3 text-left font-medium text-gray-700 hover:bg-gray-50"
            >
              <span>Signature Tracking</span>
              {signatureSectionOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {signatureSectionOpen && (
              <div className="px-4 pb-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Row 1: verbalOrderDate, datePocSentToPhysician */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Verbal Order Date</label>
                  <input type="date"
                    value={editingCert?.verbalOrderDate || ''}
                    onChange={e => setEditingCert(prev => prev ? { ...prev, verbalOrderDate: e.target.value } : null)}
                    disabled={lockFields}
                    className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm ${lockFields ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date POC Sent to Physician</label>
                  <input type="date"
                    value={editingCert?.datePocSentToPhysician || ''}
                    onChange={e => setEditingCert(prev => prev ? { ...prev, datePocSentToPhysician: e.target.value } : null)}
                    disabled={lockFields}
                    className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm ${lockFields ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                  />
                </div>
                {/* Row 2: methodPocSent, physicianSignatureDate */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Method POC Sent</label>
                  <select
                    value={editingCert?.methodPocSent || ''}
                    onChange={e => setEditingCert(prev => prev ? { ...prev, methodPocSent: e.target.value as MethodPocSent } : null)}
                    disabled={lockFields}
                    className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm ${lockFields ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                  >
                    <option value="">Select method...</option>
                    <option value="fax">Fax</option>
                    <option value="mail">Mail</option>
                    <option value="portal">Portal</option>
                    <option value="hand_delivered">Hand Delivered</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Physician Signature Date</label>
                  <input type="date"
                    value={editingCert?.physicianSignatureDate || ''}
                    onChange={e => setEditingCert(prev => prev ? { ...prev, physicianSignatureDate: e.target.value } : null)}
                    disabled={lockSignature}
                    className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm ${lockSignature ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                  />
                </div>
                {/* Row 3: dateSignedPotReceived + 14-day rule badge */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date Agency Received Signed POT</label>
                  <input type="date"
                    value={editingCert?.dateSignedPotReceived || ''}
                    onChange={e => setEditingCert(prev => prev ? { ...prev, dateSignedPotReceived: e.target.value } : null)}
                    disabled={lockSignature}
                    className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm ${lockSignature ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                  />
                </div>
                <div className="flex items-end">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">14-Day Rule Status</label>
                    {editingCert?.fourteenDayRuleLabel ? (
                      <span className={`px-2 py-1 rounded text-xs inline-block ${POCCertificationsService.getFourteenDayBadgeStyles(editingCert.fourteenDayRuleStatus)}`}>
                        {editingCert.fourteenDayRuleLabel}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </div>
                </div>
                {/* Row 4: careCoordinatorName, careCoordinatorSignatureDate */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Care Coordinator Name</label>
                  <input type="text"
                    value={editingCert?.careCoordinatorName || ''}
                    onChange={e => setEditingCert(prev => prev ? { ...prev, careCoordinatorName: e.target.value } : null)}
                    disabled={lockSignature}
                    className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm ${lockSignature ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Care Coordinator Signature Date</label>
                  <input type="date"
                    value={editingCert?.careCoordinatorSignatureDate || ''}
                    onChange={e => setEditingCert(prev => prev ? { ...prev, careCoordinatorSignatureDate: e.target.value } : null)}
                    disabled={lockSignature}
                    className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm ${lockSignature ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Linked Clinical Documents Section */}
          <div className="border border-gray-200 rounded-lg">
            <button
              type="button"
              onClick={() => void handleLinkedDocsSectionOpen()}
              className="w-full flex items-center justify-between px-4 py-3 text-left font-medium text-gray-700 hover:bg-gray-50"
            >
              <span>Linked Clinical Documents</span>
              {linkedDocsSectionOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {linkedDocsSectionOpen && (
              <div className="px-4 pb-4">
                {linkedEntityLoading ? (
                  <LoadingSpinner className="py-4" />
                ) : (
                  <div className="space-y-4">
                    {(editingCert?.disciplines || [editingCert?.discipline]).filter(Boolean).map((uiDisc) => {
                      const apiDisc = mapUiDisciplineToApiDiscipline(uiDisc as string);
                      const assessmentOptions = linkedEntityAssessments[uiDisc as string] || linkedEntityAssessments[apiDisc] || [];
                      const carePlanOptions = linkedEntityCarePlans[uiDisc as string] || linkedEntityCarePlans[apiDisc] || [];
                      return (
                        <div key={uiDisc as string} className="space-y-2">
                          <h4 className="text-sm font-semibold text-gray-600">{uiDisc as string}</h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {/* Phase 20B: aides have no assessment; their plan is written by the RN or therapist */}
                            {getDisciplineEntry(apiDisc)?.forms.assessment !== false && (
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Linked Assessment</label>
                              <select
                                value={editingCert?.linkedAssessments?.[apiDisc as keyof typeof editingCert.linkedAssessments] || ''}
                                onChange={e => setEditingCert(prev => prev ? {
                                  ...prev,
                                  linkedAssessments: { ...prev.linkedAssessments, [apiDisc]: e.target.value || undefined }
                                } : null)}
                                disabled={lockFields}
                                className={`w-full border border-gray-300 rounded-md px-3 py-2 text-xs ${lockFields ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                              >
                                <option value="">None</option>
                                {assessmentOptions.map((a: any) => (
                                  <option key={a.assessmentId || a.id} value={a.assessmentId || a.id}>
                                    {a.assessmentDate || a.createdAt?.split('T')[0]} - {a.assessmentType || uiDisc as string} ({a.status}) - {a.providerName || ''}
                                  </option>
                                ))}
                              </select>
                            </div>
                            )}
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Linked Care Plan</label>
                              <select
                                value={editingCert?.linkedCarePlans?.[apiDisc as keyof typeof editingCert.linkedCarePlans] || ''}
                                onChange={e => setEditingCert(prev => prev ? {
                                  ...prev,
                                  linkedCarePlans: { ...prev.linkedCarePlans, [apiDisc]: e.target.value || undefined }
                                } : null)}
                                disabled={lockFields}
                                className={`w-full border border-gray-300 rounded-md px-3 py-2 text-xs ${lockFields ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                              >
                                <option value="">None</option>
                                {carePlanOptions.map((cp: any) => (
                                  <option key={cp.carePlanId || cp.id} value={cp.carePlanId || cp.id}>
                                    {cp.certificationPeriodStart || cp.createdAt?.split('T')[0]} - {cp.discipline || uiDisc as string} ({cp.status}) - {cp.providerName || ''}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Generated POC Content */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2 text-left">
              Generated CMS-485 Forms
            </label>
            {versions.length > 0 ? (
              <ul className="space-y-2 mb-2" data-testid="cms485-versions">
                {versions.map((v) => (
                  <li key={`${v.discipline}-${v.version}`} className="flex flex-wrap items-center justify-between gap-2 p-3 bg-white border border-slate-200 rounded-md">
                    <div className="min-w-0 flex-1 text-left">
                      <div className="text-sm text-slate-700">
                        v{v.version} · {v.discipline}{v.legacy ? ' (earlier format)' : ''}
                      </div>
                      <div className="text-xs text-slate-500">Generated {formatDateTime(v.generatedAt)}</div>
                    </div>
                    <div className="flex gap-2">
                      {v.pdfFileId && (
                        <button type="button" onClick={() => void openCertFile(v.pdfFileId!, `CMS-485-v${v.version}.pdf`)}
                          className="inline-flex min-h-[44px] items-center gap-1 text-sm text-blue-600 hover:text-blue-700 sm:min-h-0" aria-label={`Download CMS-485 version ${v.version} ${v.discipline} PDF`}>
                          <Download size={14} aria-hidden="true" /> PDF
                        </button>
                      )}
                      {!v.pdfFileId && v.jsonFileId && (
                        <button type="button" onClick={() => void openCertFile(v.jsonFileId!, `CMS-485-v${v.version}.json`)}
                          className="inline-flex min-h-[44px] items-center gap-1 text-sm text-blue-600 hover:text-blue-700 sm:min-h-0" aria-label={`Download CMS-485 version ${v.version} ${v.discipline} data`}>
                          <Download size={14} aria-hidden="true" /> Data
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            ) : editingCert.generatedFiles && editingCert.generatedFiles.length > 0 ? (
              <ul className="space-y-2 mb-2">
                {editingCert.generatedFiles.map((file) => (
                  <li key={file.fileId} className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-md">
                    <div className="flex items-center space-x-2 flex-1 min-w-0">
                      <FileText size={16} className="text-slate-500" />
                      <div className="flex-1 min-w-0 text-left">
                        <div className="text-sm text-slate-700 truncate">{file.fileName}</div>
                        <div className="text-xs text-slate-500">{file.discipline} • Generated {formatDateTime(file.generatedAt)}</div>
                      </div>
                    </div>
                    <button type="button" onClick={() => void openCertFile(file.fileId, file.fileName)}
                      className="text-blue-600 hover:text-blue-700" title="Download generated form" aria-label="Download generated form">
                      <Download size={16} />
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-sm text-gray-500 italic mb-2">No forms generated yet</div>
            )}
            <div className="flex items-center space-x-2">
              <PrimaryButtonLight
                type="button"
                onClick={handleGeneratePOC}
                disabled={!editingCert || isGenerating || loading || isReadOnlyView}
              >
                <div className="flex items-center">
                  <RefreshCw size={16} className={`mr-2 ${isGenerating ? 'animate-spin' : ''}`} />
                  <span>
                    {isGenerating
                      ? 'Generating...'
                      : (editingCert?.generatedFiles && editingCert.generatedFiles.length > 0) || editingCert?.generatedPOCContent ? 'Regenerate' : 'Generate'}
                  </span>
                </div>
              </PrimaryButtonLight>
            </div>
          </div>

          {/* Phase 19 F17: the agency "Nursing Addendum Summary" was retired; the TMHP F00120 (Forms) carries the nursing addendum */}

          {/* Physician-signed copy (pre-signed upload, Phase 8) */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700 text-left">Signed Plan of Care</label>
            {signedDocs.length > 0 ? (
              <DocumentUpload
                canUpload={false}
                documents={signedDocs}
                onDownload={handleDownloadSignedDocument}
                onDelete={anyRemovable ? (d) => setRemoveDoc(d) : undefined}
                deleteLabel="Remove document"
                deletingId={removingDocId}
              />
            ) : (
              <p className="text-sm text-gray-500 italic">No signed copy uploaded yet.</p>
            )}
            {isEditing && !lockSignature && canUpdatePocCertification && (
              <SignedCopyUpload onUpload={handleUploadSignedCopy} disabled={actionBusy} />
            )}
            {!isEditing && (
              <p className="text-xs text-slate-500">Save the certification to upload the physician-signed copy.</p>
            )}
          </div>
        </div>

        <ConfirmDialog
          open={confirmAction === 'send'}
          title="Send for signature?"
          message="The plan of care is locked for editing while it waits for the physician. Only signature details and the signed copy can be added."
          confirmLabel="Send for signature"
          busy={actionBusy}
          onConfirm={() => runTransition('send')}
          onCancel={() => setConfirmAction(null)}
        />
        <ConfirmDialog
          open={!!removeDoc}
          title="Remove this document?"
          message={`"${removeDoc?.name ?? 'Document'}" will be removed from this draft certification.`}
          confirmLabel="Remove"
          destructive
          busy={!!removingDocId}
          onConfirm={confirmRemoveDocument}
          onCancel={() => { if (!removingDocId) setRemoveDoc(null); }}
        />
        <ConfirmDialog
          open={confirmAction === 'reject'}
          title="Reject this plan of care?"
          message="Use this when the physician declines or asks for changes. The certification becomes read-only; create a new version to send a corrected plan."
          confirmLabel="Reject"
          destructive
          busy={actionBusy}
          onConfirm={() => runTransition('reject')}
          onCancel={() => setConfirmAction(null)}
        />
        <Dialog open={showSignDialog} onClose={() => { if (!actionBusy) setShowSignDialog(false); }} labelledBy="poc-sign-title">
          <div className="text-left space-y-3">
            <h3 id="poc-sign-title" className="text-lg font-semibold text-slate-800 pr-6">Mark signed</h3>
            <p className="text-sm text-slate-600">Pick the uploaded copy that carries the physician&apos;s signature.</p>
            <p className="text-xs font-medium text-amber-800">Physicians must sign; stamps are not accepted.</p>
            <label htmlFor="poc-signed-doc" className="block text-sm font-medium text-slate-700">Signed document</label>
            <select
              id="poc-signed-doc"
              value={signedDocChoice}
              onChange={(e) => setSignedDocChoice(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="">Select a document…</option>
              {liveDocuments(editingCert.documents).map((d) => (
                <option key={d.documentId} value={d.documentId}>
                  {d.documentName || 'Document'}{d.uploadedAt ? ` (${new Date(d.uploadedAt).toLocaleDateString()})` : ''}
                </option>
              ))}
            </select>
            <div className="flex flex-wrap justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowSignDialog(false)} disabled={actionBusy}
                className="min-h-[44px] px-4 py-2 rounded-md text-sm border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-60">
                Cancel
              </button>
              <button type="button" onClick={() => void runTransition('sign')} disabled={!signedDocChoice || actionBusy}
                className="min-h-[44px] px-4 py-2 rounded-md text-sm bg-green-700 text-white hover:bg-green-800 disabled:opacity-50">
                {actionBusy ? 'Working...' : 'Mark signed'}
              </button>
            </div>
          </div>
        </Dialog>

      </FormPageLayout>
    );
  }

  return (
    <GenericListView
      // ListView props
      title="POC Certifications"
      icon={<ClipboardList size={20} />}
      expanded={expanded}
      onToggleExpand={toggleExpanded}
      alertBanner={alertBanner}
      // Data props
      items={certifications}
      keyExtractor={(cert) => cert.id.toString()}
      columns={[
        {
          key: 'disciplines',
          label: 'Disciplines',
          width: 'w-48',
          render: (cert) => {
            const disciplines = cert.disciplines || [cert.discipline].filter(Boolean);
            return (
              <div>
                <div className="flex flex-wrap gap-1">
                  {disciplines.map((disc, idx) => (
                    <span key={idx} className={`inline-block px-2 py-1 text-xs font-medium rounded ${getDisciplinePillClassName(disc)}`}>
                      {disc}
                    </span>
                  ))}
                </div>
                <div className="text-xs text-gray-500 mt-1">{cert.patientName}</div>
              </div>
            );
          },
        },
        {
          key: 'status',
          label: 'Status',
          width: 'w-24',
          align: 'center',
          render: (cert) => (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeStyles(cert.status)}`}>
              {POC_STATUS_LABELS[cert.status] ?? cert.status}
            </span>
          ),
        },
        {
          key: 'unsigned',
          label: 'Unsigned',
          width: 'w-28',
          align: 'center' as const,
          render: (cert: POCCertification) => {
            const deadline = pocUnsignedDeadline(cert);
            return deadline ? <UnsignedBadge deadline={deadline} /> : <span className="text-xs text-gray-400">—</span>;
          }
        },
        {
          key: 'signatureStatus',
          label: 'Physician Sig.',
          width: 'w-28',
          align: 'center' as const,
          render: (cert: POCCertification) => {
            const badgeClass = POCCertificationsService.getFourteenDayBadgeStyles(cert.fourteenDayRuleStatus);
            return (
              <span className={`px-2 py-1 rounded text-xs ${badgeClass}`}>
                {cert.fourteenDayRuleLabel || '—'}
              </span>
            );
          }
        },
        {
          key: 'expiration',
          label: 'Expires',
          width: 'w-28',
          align: 'center' as const,
          render: (cert: POCCertification) => {
            const badgeClass = POCCertificationsService.getExpirationBadgeStyles(cert.expirationAlertLevel);
            const label = cert.expirationIsExpired
              ? 'Expired'
              : `${cert.expirationDaysRemaining ?? '?'} days`;
            return (
              <span className={`px-2 py-1 rounded text-xs ${badgeClass}`}>
                {label}
              </span>
            );
          }
        },
        {
          key: 'dates',
          label: 'Certification Period',
          width: 'w-40',
          render: (cert) => (
            <div className="text-sm">
              <div>{formatDate(cert.certificationPeriodStart)}</div>
              <div className="mt-1">{formatDate(cert.certificationPeriodEnd)}</div>
            </div>
          ),
        },
        {
          key: 'document',
          label: 'Signed Doc',
          width: 'w-24',
          align: 'center',
          render: (cert) => (
            liveDocuments(cert.documents).length > 0 ? (
              <FileText size={16} className="text-green-600 mx-auto" />
            ) : (
              <span className="text-xs text-gray-400">-</span>
            )
          ),
        },
      ]}
      actions={[
        ...(canUpdatePocCertification ? [{
          icon: <Edit size={14} />,
          label: 'Edit certification',
          onClick: (cert) => openEditForm(cert.id),
          className: 'p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors',
        }] : [{
          icon: <Edit size={14} />,
          label: 'View certification',
          onClick: (cert) => openEditForm(cert.id),
          className: 'p-1.5 text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded transition-colors',
        }]),
        // Note: Delete action is intentionally not included for POC Certifications
        // There is no delete permission for POC Certifications
      ]}
      // Mobile card config
      mobileCard={{
        primaryText: (cert) => 'POC Certification',
        secondaryText: (cert) => `${formatDate(cert.certificationPeriodStart)} - ${formatDate(cert.certificationPeriodEnd)}`,
        tertiaryText: (cert) => (cert.orderReceivedDate ? `Order received ${formatDate(cert.orderReceivedDate)}` : undefined),
        badges: (cert) => {
          const disciplines = cert.disciplines || [cert.discipline].filter(Boolean);
          const statusFormatted = POC_STATUS_LABELS[cert.status] ?? cert.status;
          return [
            ...disciplines.map(disc => ({
              label: disc,
              className: getDisciplinePillClassName(disc),
            })),
            {
              label: statusFormatted,
              className: getStatusBadgeStyles(cert.status),
            },
            ...(() => {
              const deadline = pocUnsignedDeadline(cert);
              if (!deadline) return [];
              const cls = deadline.level === 'red'
                ? 'bg-red-100 text-red-800 border border-red-300'
                : deadline.level === 'amber'
                  ? 'bg-amber-100 text-amber-800 border border-amber-300'
                  : 'bg-slate-100 text-slate-700';
              return [{ label: `Unsigned ${deadline.days}d`, className: cls }];
            })(),
          ];
        },
        actions: (cert) => [
          ...(canUpdatePocCertification ? [{
            icon: <Edit size={18} />,
            label: 'Edit',
            onClick: () => openEditForm(cert.id),
          }] : [{
            icon: <Edit size={18} />,
            label: 'View',
            onClick: () => openEditForm(cert.id),
          }]),
          // Note: Delete action is intentionally not included for POC Certifications
          // There is no delete permission for POC Certifications
        ],
      }}
      // Action bar props
      onRefresh={handleRefresh}
      refreshing={refreshing}
      loading={loading}
      newButtonLabel="New POC Certification"
      onNewClick={() => void openAddForm()}
      showNew={canCreatePocCertification}
      // State props
      error={error}
      onDismissError={() => setError(null)}
      hasLoadedOnce={hasLoadedOnce}
      // Empty state props
      emptyStateIcon={<ClipboardList size={48} className="text-gray-300" />}
      emptyStateTitle="No POC certifications available"
      emptyStateMessage="Start by creating a new POC certification using the button above."
    />
  );
};

export default POCCertifications;
